/**
 * 👑 Admin User Management
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    role: 'user',
    license_type: 'permanent',
    proxy_pool_mode: 'system',
    is_active: true,
    mailgateway_enabled: true
  });
  const [form, setForm] = useState({
    username: '',
    password: '',
    role: 'user',
    license_type: 'permanent',
    proxy_pool_mode: 'system'
  });
  const [proxyFile, setProxyFile] = useState(null);
  const [proxyUser, setProxyUser] = useState(null);
  const [proxyText, setProxyText] = useState('');
  const [proxyList, setProxyList] = useState([]);
  const [proxyLoading, setProxyLoading] = useState(false);

  const loadUsers = async () => {
    setLoadingUsers(true);
    try {
      const response = await axios.get('/api/admin/users');
      if (response.data?.success) {
        setUsers(response.data.data || []);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Konnte User nicht laden');
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0] || null;
    setProxyFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password) {
      toast.error('Username und Passwort sind erforderlich');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post('/api/admin/users', {
        username: form.username.trim(),
        password: form.password,
        role: form.role,
        license_type: form.license_type,
        proxy_pool_mode: form.proxy_pool_mode
      });

      if (response.data?.success) {
        const createdUser = response.data.data;
        if (form.proxy_pool_mode === 'custom' && proxyFile) {
          const formData = new FormData();
          formData.append('file', proxyFile);
          try {
            await axios.post(
              `/api/admin/users/${createdUser.telegram_user_id}/proxy-pool/import`,
              formData,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            toast.success('Proxy-Pool importiert');
          } catch (importError) {
            toast.error(importError.response?.data?.error || 'Proxy-Pool Import fehlgeschlagen');
          }
        }
        toast.success('User angelegt');
        setForm((prev) => ({ ...prev, password: '' }));
        setProxyFile(null);
        await loadUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'User konnte nicht angelegt werden');
    } finally {
      setSubmitting(false);
    }
  };

  const startEdit = (user) => {
    setEditingUser(user);
    setEditForm({
      role: user.role || 'user',
      license_type: user.license_type || 'permanent',
      proxy_pool_mode: user.proxy_pool_mode || 'system',
      is_active: !!user.is_active,
      mailgateway_enabled: !!user.mailgateway_enabled
    });
  };

  const saveEdit = async () => {
    if (!editingUser) return;
    try {
      await axios.put(`/api/admin/users/${editingUser.telegram_user_id}`, {
        role: editForm.role,
        license_type: editForm.license_type,
        proxy_pool_mode: editForm.proxy_pool_mode,
        is_active: editForm.is_active ? 1 : 0,
        mailgateway_enabled: editForm.mailgateway_enabled ? 1 : 0
      });
      toast.success('User aktualisiert');
      setEditingUser(null);
      await loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Update fehlgeschlagen');
    }
  };

  const toggleActive = async (user) => {
    try {
      const nextActive = !user.is_active;
      await axios.put(`/api/admin/users/${user.telegram_user_id}`, {
        is_active: nextActive ? 1 : 0,
        mailgateway_enabled: nextActive ? 1 : 0
      });
      toast.success(nextActive ? 'User aktiviert' : 'User gesperrt');
      await loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Statusänderung fehlgeschlagen');
    }
  };

  const deleteUser = async (user) => {
    const confirmed = window.confirm(`User ${user.username_login} wirklich löschen?`);
    if (!confirmed) return;
    try {
      await axios.delete(`/api/admin/users/${user.telegram_user_id}`);
      toast.success('User gelöscht');
      await loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Löschen fehlgeschlagen');
    }
  };

  const loadProxyPool = async (user) => {
    setProxyUser(user);
    setProxyLoading(true);
    try {
      const response = await axios.get(`/api/admin/users/${user.telegram_user_id}/proxy-pool`);
      if (response.data?.success) {
        setProxyList(response.data.data || []);
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Proxy-Pool konnte nicht geladen werden');
    } finally {
      setProxyLoading(false);
    }
  };

  const addProxyText = async () => {
    if (!proxyUser || !proxyText.trim()) {
      toast.error('Bitte Proxy-Text eingeben');
      return;
    }
    try {
      await axios.post(`/api/admin/users/${proxyUser.telegram_user_id}/proxy-pool`, {
        proxies_text: proxyText
      });
      toast.success('Proxies gespeichert');
      setProxyText('');
      await loadProxyPool(proxyUser);
      await loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Proxy-Pool konnte nicht gespeichert werden');
    }
  };

  const uploadProxyFile = async (file) => {
    if (!proxyUser || !file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await axios.post(
        `/api/admin/users/${proxyUser.telegram_user_id}/proxy-pool/import`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Proxy-Pool importiert');
      await loadProxyPool(proxyUser);
      await loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Proxy-Pool Import fehlgeschlagen');
    }
  };

  const deleteProxy = async (proxyId) => {
    if (!proxyUser) return;
    try {
      await axios.delete(`/api/admin/users/${proxyUser.telegram_user_id}/proxy-pool/${proxyId}`);
      toast.success('Proxy gelöscht');
      await loadProxyPool(proxyUser);
      await loadUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Proxy konnte nicht gelöscht werden');
    }
  };

  const renderDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('de-DE');
  };

  return (
    <div className="space-y-6">
      <div className="bg-white dark:bg-dark-secondary shadow rounded-lg p-6 border dark:border-dark-copper-dark">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          👑 Admin: Benutzerverwaltung
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          User erstellen, Rolle und Lizenz setzen. Mailgateway ist standardmäßig aktiv.
        </p>
      </div>

      <div className="bg-white dark:bg-dark-secondary shadow rounded-lg p-6 border dark:border-dark-copper-dark">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Neuen Benutzer anlegen
        </h2>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Username
            </label>
            <input
              type="text"
              name="username"
              value={form.username}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 dark:border-dark-copper-dark rounded-lg bg-white dark:bg-dark-surface text-gray-900 dark:text-white"
              placeholder="z.B. kundenname"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Passwort
            </label>
            <input
              type="password"
              name="password"
              value={form.password}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 dark:border-dark-copper-dark rounded-lg bg-white dark:bg-dark-surface text-gray-900 dark:text-white"
              placeholder="Sicheres Passwort"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Rolle
            </label>
            <select
              name="role"
              value={form.role}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 dark:border-dark-copper-dark rounded-lg bg-white dark:bg-dark-surface text-gray-900 dark:text-white"
            >
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Lizenz
            </label>
            <select
              name="license_type"
              value={form.license_type}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 dark:border-dark-copper-dark rounded-lg bg-white dark:bg-dark-surface text-gray-900 dark:text-white"
            >
              <option value="week">Woche</option>
              <option value="month">Monat</option>
              <option value="permanent">Permanent</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Proxy-Modus
            </label>
            <select
              name="proxy_pool_mode"
              value={form.proxy_pool_mode}
              onChange={handleChange}
              className="w-full px-4 py-2 border border-gray-300 dark:border-dark-copper-dark rounded-lg bg-white dark:bg-dark-surface text-gray-900 dark:text-white"
            >
              <option value="system">System-Proxies</option>
              <option value="custom">Eigener Proxy-Pool</option>
            </select>
          </div>
          {form.proxy_pool_mode === 'custom' && (
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Proxy-CSV Upload
              </label>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-copper-dark rounded-lg bg-white dark:bg-dark-surface text-gray-900 dark:text-white"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Format: host,port,username,password,resetUrl(optional),label(optional)
              </p>
            </div>
          )}
          <div className="md:col-span-2">
            <button
              type="submit"
              disabled={submitting}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
            >
              {submitting ? 'Speichern...' : 'User anlegen'}
            </button>
          </div>
        </form>
      </div>

      <div className="bg-white dark:bg-dark-secondary shadow rounded-lg p-6 border dark:border-dark-copper-dark">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Benutzerliste
          </h2>
          <button
            onClick={loadUsers}
            disabled={loadingUsers}
            className="text-sm px-3 py-2 border border-gray-300 dark:border-dark-copper-dark rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-surface transition-colors"
          >
            {loadingUsers ? 'Laden...' : 'Aktualisieren'}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 dark:text-gray-400">
                <th className="py-2 pr-4">Username</th>
                <th className="py-2 pr-4">Rolle</th>
                <th className="py-2 pr-4">Proxy</th>
                <th className="py-2 pr-4">Pool</th>
                <th className="py-2 pr-4">Lizenz</th>
                <th className="py-2 pr-4">Ablauf</th>
                <th className="py-2 pr-4">Gateway</th>
                <th className="py-2 pr-4">Aktiv</th>
                <th className="py-2 pr-4">Erstellt</th>
                <th className="py-2 pr-4">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr>
                  <td colSpan="10" className="py-4 text-gray-500 dark:text-gray-400">
                    Keine Benutzer gefunden
                  </td>
                </tr>
              )}
              {users.map((user) => (
                <tr key={user.telegram_user_id} className="border-t border-gray-100 dark:border-dark-copper-dark">
                  <td className="py-2 pr-4 text-gray-900 dark:text-gray-100">
                    {user.username_login}
                  </td>
                  <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">
                    {user.role}
                  </td>
                  <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">
                    {user.proxy_pool_mode === 'custom' ? 'Custom' : 'System'}
                  </td>
                  <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">
                    {user.proxy_count ?? 0}
                  </td>
                  <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">
                    {user.license_type || 'permanent'}
                  </td>
                  <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">
                    {user.license_type === 'permanent' ? '—' : renderDate(user.license_expires_at)}
                  </td>
                  <td className="py-2 pr-4">
                    <span className={user.mailgateway_enabled ? 'text-green-600' : 'text-red-600'}>
                      {user.mailgateway_enabled ? 'aktiv' : 'aus'}
                    </span>
                  </td>
                  <td className="py-2 pr-4">
                    <span className={user.is_active ? 'text-green-600' : 'text-red-600'}>
                      {user.is_active ? 'ja' : 'nein'}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">
                    {renderDate(user.created_at)}
                  </td>
                  <td className="py-2 pr-4">
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => startEdit(user)}
                        className="px-2 py-1 text-xs border border-gray-300 dark:border-dark-copper-dark rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-surface"
                      >
                        Bearbeiten
                      </button>
                      <button
                        onClick={() => toggleActive(user)}
                        className="px-2 py-1 text-xs border border-gray-300 dark:border-dark-copper-dark rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-surface"
                      >
                        {user.is_active ? 'Sperren' : 'Aktivieren'}
                      </button>
                      <button
                        onClick={() => loadProxyPool(user)}
                        className="px-2 py-1 text-xs border border-gray-300 dark:border-dark-copper-dark rounded text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-dark-surface"
                      >
                        Proxies
                      </button>
                      <button
                        onClick={() => deleteUser(user)}
                        className="px-2 py-1 text-xs border border-red-300 dark:border-red-700 rounded text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        Löschen
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editingUser && (
        <div className="bg-white dark:bg-dark-secondary shadow rounded-lg p-6 border dark:border-dark-copper-dark">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            User bearbeiten: {editingUser.username_login}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Rolle
              </label>
              <select
                name="role"
                value={editForm.role}
                onChange={handleEditChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-dark-copper-dark rounded-lg bg-white dark:bg-dark-surface text-gray-900 dark:text-white"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Lizenz
              </label>
              <select
                name="license_type"
                value={editForm.license_type}
                onChange={handleEditChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-dark-copper-dark rounded-lg bg-white dark:bg-dark-surface text-gray-900 dark:text-white"
              >
                <option value="week">Woche</option>
                <option value="month">Monat</option>
                <option value="permanent">Permanent</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Proxy-Modus
              </label>
              <select
                name="proxy_pool_mode"
                value={editForm.proxy_pool_mode}
                onChange={handleEditChange}
                className="w-full px-4 py-2 border border-gray-300 dark:border-dark-copper-dark rounded-lg bg-white dark:bg-dark-surface text-gray-900 dark:text-white"
              >
                <option value="system">System-Proxies</option>
                <option value="custom">Eigener Proxy-Pool</option>
              </select>
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  name="mailgateway_enabled"
                  checked={editForm.mailgateway_enabled}
                  onChange={handleEditChange}
                />
                Mailgateway aktiv
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={editForm.is_active}
                  onChange={handleEditChange}
                />
                User aktiv
              </label>
            </div>
            <div className="md:col-span-2 flex gap-2">
              <button
                onClick={saveEdit}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Speichern
              </button>
              <button
                onClick={() => setEditingUser(null)}
                className="border border-gray-300 dark:border-dark-copper-dark text-gray-700 dark:text-gray-300 px-4 py-2 rounded-md text-sm font-medium"
              >
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}

      {proxyUser && (
        <div className="bg-white dark:bg-dark-secondary shadow rounded-lg p-6 border dark:border-dark-copper-dark">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Proxy-Pool: {proxyUser.username_login}
            </h2>
            <button
              onClick={() => setProxyUser(null)}
              className="text-sm border border-gray-300 dark:border-dark-copper-dark rounded px-3 py-1 text-gray-700 dark:text-gray-300"
            >
              Schließen
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Proxies per Text
              </label>
              <textarea
                value={proxyText}
                onChange={(e) => setProxyText(e.target.value)}
                rows="6"
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-copper-dark rounded-lg bg-white dark:bg-dark-surface text-gray-900 dark:text-white"
                placeholder="host,port,username,password,resetUrl(optional),label(optional)"
              />
              <button
                onClick={addProxyText}
                className="mt-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Speichern
              </button>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Proxy-CSV Upload
              </label>
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => uploadProxyFile(e.target.files?.[0])}
                className="w-full px-3 py-2 border border-gray-300 dark:border-dark-copper-dark rounded-lg bg-white dark:bg-dark-surface text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-4">Host</th>
                  <th className="py-2 pr-4">Port</th>
                  <th className="py-2 pr-4">User</th>
                  <th className="py-2 pr-4">Label</th>
                  <th className="py-2 pr-4"></th>
                </tr>
              </thead>
              <tbody>
                {proxyLoading && (
                  <tr>
                    <td colSpan="5" className="py-4 text-gray-500 dark:text-gray-400">
                      Lade Proxies...
                    </td>
                  </tr>
                )}
                {!proxyLoading && proxyList.length === 0 && (
                  <tr>
                    <td colSpan="5" className="py-4 text-gray-500 dark:text-gray-400">
                      Keine Proxies vorhanden
                    </td>
                  </tr>
                )}
                {proxyList.map((proxy) => (
                  <tr key={proxy.id} className="border-t border-gray-100 dark:border-dark-copper-dark">
                    <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{proxy.host}</td>
                    <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{proxy.port}</td>
                    <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{proxy.username}</td>
                    <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">{proxy.label || '—'}</td>
                    <td className="py-2 pr-4">
                      <button
                        onClick={() => deleteProxy(proxy.id)}
                        className="text-xs text-red-600 hover:text-red-700"
                      >
                        Löschen
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
